import { MelodyLib } from '@tunasong/music-lib'
import type { MelodyChordLane, MelodyMeter } from '@tunasong/schemas'
import { Button, Menu, MenuContent, MenuItem, ToggleButton } from '@tunasong/ui-lib'
import type { FC, KeyboardEvent, PointerEvent } from 'react'
import { useRef, useState } from 'react'
import { AddChordsField } from './add-chords-field.js'
import styles from './chord-lane.module.css'
import { beatX } from './melody-roll-geometry.js'

/**
 * The chord lane over the melody roll (melody toolkit plan M1 step 1.4; spec §4.2, §8, §9, §10): one
 * button per chord, sitting over its own beats, with an end edge the singer drags to match the song.
 *
 * Its timing starts as a guess, one chord per bar, drawn dashed and saying so, until the singer moves an
 * edge or presses `Looks right`. Without `onChange` it is read-only: the chords and their bars, and no way
 * to move them. Every rule about timing is `MelodyLib`'s; this draws it and turns gestures into calls.
 */

export interface ChordLaneProps {
  /** The resolved lane: `MelodyLib.resolveChordLane` of the chart and what the melody stored */
  lane: MelodyChordLane
  meter: MelodyMeter
  /** The roll's length in beats, so each chord sits over the notes it sounds under */
  totalBeats: number
  /** Inside a section an empty lane says the section has no chords; outside one it shows nothing */
  isInSection: boolean
  /** Omit for a read-only lane */
  onChange?: (lane: MelodyChordLane) => void
  /** The standalone sketch: an empty lane offers `Add chords` (melody toolkit M9.2; spec §7) */
  canAddChords?: boolean
  /**
   * Spec §8: a section without chords offers `Drone`, the key's root and fifth under the melody. Absent
   * when there is no key to drone on (melody toolkit M2)
   */
  drone?: { isOn: boolean; onChange: (isOn: boolean) => void }
  /** A click on a chord: the editor selects the notes whose onsets fall in it (melody toolkit M6, D7) */
  onChordSelect?: (index: number) => void
  className?: string
}

export interface ChordLaneGuessProps {
  lane: MelodyChordLane
  /** Omit for a read-only lane: no guess line, nothing to confirm */
  onChange?: (lane: MelodyChordLane) => void
  className?: string
}

/**
 * §4.2: while the lane's timing is a guess, a line says so with `Looks right` to confirm it. Its own piece,
 * so the editor can keep it out of the lane's sideways scroll on a phone (Joni, M1–M5 review).
 */
export const ChordLaneGuess: FC<ChordLaneGuessProps> = props => {
  const { lane, onChange, className } = props
  if (!onChange || lane.isConfirmed || lane.chords.length === 0) {
    return null
  }
  return (
    <div className={className ? `${styles.guess} ${className}` : styles.guess}>
      <span>Chord timing is a guess. Drag the edges to match your song.</span>
      <Button size="small" onClick={() => onChange({ ...lane, isConfirmed: true })}>
        Looks right
      </Button>
    </div>
  )
}

/** Long enough not to fire on a tap, short enough to feel like a press (the platform's own is ~500 ms) */
const LONG_PRESS_MS = 500

interface Drag {
  index: number
  startX: number
  width: number
  lane: MelodyChordLane
  steps: number
}

export const ChordLane: FC<ChordLaneProps> = props => {
  const {
    lane,
    meter,
    totalBeats,
    isInSection,
    onChange,
    canAddChords = false,
    drone,
    onChordSelect,
    className,
  } = props
  const listRef = useRef<HTMLOListElement>(null)
  const pressTimer = useRef<number | null>(null)
  const drag = useRef<Drag | null>(null)
  /** The chord whose long-press offer is open, and the button it is anchored to */
  const [nudging, setNudging] = useState<{ index: number; anchor: HTMLElement } | null>(null)
  /**
   * A drag's lane while it moves: drawn, never written, so a drag is one edit and one undo step however
   * many beats it crosses (Joni, M1–M5 review)
   */
  const [preview, setPreview] = useState<MelodyChordLane | null>(null)
  const shown = preview ?? lane

  const droneToggle = drone ? (
    <ToggleButton value="drone" size="small" selected={drone.isOn} onClick={() => drone.onChange(!drone.isOn)}>
      Drone
    </ToggleButton>
  ) : null

  if (lane.chords.length === 0 && canAddChords && onChange && !isInSection) {
    /** Spec §7: chords come from the singer, typed, or there are none and the key drones */
    return (
      <div className={styles.guess}>
        <AddChordsField meter={meter} onAdd={onChange} />
        {droneToggle}
      </div>
    )
  }

  if (lane.chords.length === 0) {
    return isInSection ? (
      <div className={styles.guess}>
        <p className={styles.empty}>No chords in this section.</p>
        {droneToggle}
      </div>
    ) : null
  }

  const move = (index: number, beats: number) => onChange?.(MelodyLib.moveEdge(lane, index, beats, meter))

  const handleChordKeyDown = (index: number) => (event: KeyboardEvent) => {
    if (!event.altKey || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) {
      return
    }
    event.preventDefault()
    move(index, event.key === 'ArrowLeft' ? -1 : 1)
  }

  const handleEdgeKeyDown = (index: number) => (event: KeyboardEvent) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return
    }
    event.preventDefault()
    move(index, event.key === 'ArrowLeft' ? -1 : 1)
  }

  const cancelPress = () => {
    if (pressTimer.current !== null) {
      window.clearTimeout(pressTimer.current)
      pressTimer.current = null
    }
  }

  /** §9: on touch, a long-press offers the one-beat moves, the alternative to dragging (WCAG 2.5.7) */
  const handleChordPointerDown = (index: number) => (event: PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType !== 'touch') {
      return
    }
    cancelPress()
    const anchor = event.currentTarget
    pressTimer.current = window.setTimeout(() => {
      pressTimer.current = null
      setNudging({ index, anchor })
    }, LONG_PRESS_MS)
  }

  const handleNudge = (beats: number) => {
    if (nudging) {
      move(nudging.index, beats)
    }
    setNudging(null)
  }

  const handleEdgePointerDown = (index: number) => (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture?.(event.pointerId)
    const width = listRef.current?.getBoundingClientRect().width ?? 0
    drag.current = { index, startX: event.clientX, width, lane, steps: 0 }
  }

  /** Whole felt beats from where the drag started, against the lane as it was then */
  const handleEdgePointerMove = (event: PointerEvent) => {
    const current = drag.current
    if (!current || current.width === 0) {
      return
    }
    const beats = ((event.clientX - current.startX) / current.width) * totalBeats
    const steps = Math.round(beats / MelodyLib.feltBeat(meter))
    if (steps === current.steps) {
      return
    }
    current.steps = steps
    setPreview(MelodyLib.moveEdge(current.lane, current.index, steps, meter))
  }

  /** The drag commits once, on release */
  const handleEdgePointerUp = () => {
    drag.current = null
    if (preview) {
      onChange?.(preview)
    }
    setPreview(null)
  }

  const step = MelodyLib.feltBeat(meter)
  /** The chord whose long-press menu is open: the menu is named for it */
  const nudgedChord = nudging ? lane.chords[nudging.index] : undefined

  const isEditable = onChange !== undefined

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      <ol ref={listRef} aria-label="Chords" className={styles.lane} data-confirmed={String(lane.isConfirmed)}>
        {shown.chords.map((chord, index) => {
          const label = MelodyLib.laneChordLabel(chord, meter, shown.isConfirmed)
          const end = chord.onset + chord.duration
          const next = shown.chords[index + 1]
          const isJoined = next !== undefined && Math.abs(next.onset - end) < MelodyLib.GRID_TOLERANCE
          /** How far the edge can go: a beat short of the next chord's end, or the roll's end */
          const max = next ? (isJoined ? next.onset + next.duration - step : next.onset) : totalBeats
          const edgeLabel = MelodyLib.laneEdgeLabel(chord, meter)
          return (
            <li
              key={chord.chartId ?? `${chord.symbol}-${chord.onset}`}
              className={styles.chord}
              style={{ left: `${beatX(chord.onset, totalBeats)}%`, width: `${beatX(chord.duration, totalBeats)}%` }}
            >
              {isEditable ? (
                <>
                  <button
                    type="button"
                    className={styles.name}
                    aria-label={label}
                    onClick={() => onChordSelect?.(index)}
                    onKeyDown={handleChordKeyDown(index)}
                    onPointerDown={handleChordPointerDown(index)}
                    onPointerUp={cancelPress}
                    onPointerLeave={cancelPress}
                    onPointerCancel={cancelPress}
                    onContextMenu={event => event.preventDefault()}
                  >
                    {chord.symbol}
                  </button>
                  {/* The ARIA window-splitter: announced as adjustable, its value in beats (Joni, M1–M5 review) */}
                  <div
                    role="separator"
                    tabIndex={0}
                    className={next ? styles.edge : `${styles.edge} ${styles.lastEdge}`}
                    aria-orientation="vertical"
                    aria-label={edgeLabel}
                    aria-valuetext={edgeLabel}
                    aria-valuenow={end}
                    aria-valuemin={chord.onset + step}
                    aria-valuemax={max}
                    onKeyDown={handleEdgeKeyDown(index)}
                    onPointerDown={handleEdgePointerDown(index)}
                    onPointerMove={handleEdgePointerMove}
                    onPointerUp={handleEdgePointerUp}
                    onPointerCancel={handleEdgePointerUp}
                  />
                </>
              ) : (
                <>
                  <span className={styles.name} aria-hidden="true">
                    {chord.symbol}
                  </span>
                  <span className={styles.visuallyHidden}>{label}</span>
                </>
              )}
            </li>
          )
        })}
      </ol>
      {isEditable ? (
        /* The long-press offer: a menu anchored to the chord, for focus, Esc, an outside press and the edges */
        <Menu
          open={nudging !== null}
          onOpenChange={isOpen => {
            if (!isOpen) {
              setNudging(null)
            }
          }}
        >
          <MenuContent
            anchorEl={nudging?.anchor ?? null}
            aria-label={nudgedChord ? MelodyLib.laneChordLabel(nudgedChord, meter, lane.isConfirmed) : undefined}
          >
            <MenuItem onClick={() => handleNudge(-1)}>Earlier by a beat</MenuItem>
            <MenuItem onClick={() => handleNudge(1)}>Later by a beat</MenuItem>
          </MenuContent>
        </Menu>
      ) : null}
    </div>
  )
}
